"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { Loader2 } from "lucide-react"
import { motion, useReducedMotion } from "framer-motion"
import { DockSheet } from "@/components/ui/dock-sheet"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { DialogCard } from "@/components/ui/dialog-card"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"
import { useToast } from "@/hooks/use-toast"
import { useOfferActions } from "@/hooks/use-offer"
import { formatPrice } from "@/lib/utils"
import { generateClientOfferId } from "@/lib/offers/offer-utils"
import type { OfferWithRelations } from "@/lib/offers/offer-types"

const FEED_MOTION_EASE = [0.4, 0, 0.2, 1] as const
const FEED_MOTION_MS = 0.25

export type OfferPendingResubmitPayload = {
  amount: number
  message: string | null
  expiresInHours: number
  currency: string | null
}

type OfferPendingBlockedSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  listingId: string
  initialOfferId?: string | null
  onWithdrawn: () => void
  resubmitCreateOffer?: OfferPendingResubmitPayload | null
  onResubmitSuccess?: (offerId: string) => void
}

export function OfferPendingBlockedSheet({
  open,
  onOpenChange,
  listingId,
  initialOfferId,
  onWithdrawn,
  resubmitCreateOffer = null,
  onResubmitSuccess,
}: OfferPendingBlockedSheetProps) {
  const t = useTranslations("offers.pendingBlocked")
  const tOff = useTranslations("offers.errors")
  const { toast } = useToast()
  const isMobile = useMobile()
  const reduceMotion = useReducedMotion()
  const actions = useOfferActions()
  const [offerId, setOfferId] = useState<string | null>(null)
  const [offer, setOffer] = useState<OfferWithRelations | null>(null)
  const [resolving, setResolving] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [resolveFailed, setResolveFailed] = useState(false)

  const canResubmit = Boolean(
    resubmitCreateOffer && onResubmitSuccess && resubmitCreateOffer.amount > 0
  )
  const newAmountLabel = resubmitCreateOffer
    ? formatPrice(String(resubmitCreateOffer.amount), resubmitCreateOffer.currency ?? "EUR")
    : null

  useEffect(() => {
    if (!open) {
      setOfferId(null)
      setOffer(null)
      setResolving(false)
      setWithdrawing(false)
      setResolveFailed(false)
      return
    }

    let cancelled = false

    const run = async () => {
      if (initialOfferId) {
        setResolving(true)
        setResolveFailed(false)
        try {
          const res = await fetch(`/api/offers/${encodeURIComponent(initialOfferId)}`, { credentials: "include" })
          const j = (await res.json().catch(() => null)) as { offer?: OfferWithRelations } | null
          if (cancelled) return
          if (res.ok && j?.offer) {
            setOfferId(j.offer.id)
            setOffer(j.offer)
            setResolveFailed(false)
            return
          }
        } catch {
          if (!cancelled) setResolveFailed(true)
        } finally {
          if (!cancelled) setResolving(false)
        }
        if (cancelled) return
        setResolveFailed(true)
        return
      }

      setResolving(true)
      setResolveFailed(false)
      try {
        const res = await fetch(`/api/offers?type=sent&status=pending&limit=100`, { credentials: "include" })
        const j = (await res.json().catch(() => null)) as { offers?: OfferWithRelations[] } | null
        if (cancelled) return
        const found = (j?.offers ?? []).find((o) => o.listing_id === listingId)
        if (found?.id) {
          setOfferId(found.id)
          setOffer(found)
        } else {
          setResolveFailed(true)
        }
      } catch {
        if (!cancelled) setResolveFailed(true)
      } finally {
        if (!cancelled) setResolving(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [open, initialOfferId, listingId])

  const handleWithdrawOnly = async () => {
    if (!offerId || withdrawing) return
    setWithdrawing(true)
    try {
      const { ok } = await actions.withdrawOffer(offerId)
      if (ok) {
        void actions.refetchListing(listingId)
        onWithdrawn()
        onOpenChange(false)
      }
    } finally {
      setWithdrawing(false)
    }
  }

  const handleWithdrawAndResubmit = async () => {
    if (!canResubmit || !resubmitCreateOffer || !offerId || !onResubmitSuccess || withdrawing) return
    setWithdrawing(true)
    try {
      const { ok: wOk } = await actions.withdrawOffer(offerId)
      if (!wOk) {
        toast({ title: t("resolveError"), variant: "destructive" })
        return
      }
      void actions.refetchListing(listingId)
      const result = await actions.placeOffer({
        listingId,
        amount: resubmitCreateOffer.amount,
        message: resubmitCreateOffer.message,
        expiresInHours: resubmitCreateOffer.expiresInHours,
        clientOfferId: generateClientOfferId(),
      })
      if (!result.ok) {
        onWithdrawn()
        onOpenChange(false)
        toast({ title: tOff("generic"), variant: "destructive" })
        return
      }
      const data = result.data as { offer?: { id: string }; auto_declined?: boolean }
      if (data?.auto_declined) {
        onWithdrawn()
        onOpenChange(false)
        toast({ title: tOff("belowMinimumInline"), variant: "destructive" })
        return
      }
      if (data?.offer?.id) {
        onResubmitSuccess(data.offer.id)
        onOpenChange(false)
      } else {
        onWithdrawn()
        onOpenChange(false)
      }
    } finally {
      setWithdrawing(false)
    }
  }

  const amountLabel =
    offer != null ? formatPrice(String(offer.amount), offer.currency ?? "EUR") : null

  const innerEase = {
    duration: reduceMotion ? 0.01 : FEED_MOTION_MS,
    ease: FEED_MOTION_EASE,
  }
  const innerUp = reduceMotion
    ? false
    : ({ opacity: 0, y: 6 } as { opacity: number; y: number })
  const primaryAction = canResubmit ? () => void handleWithdrawAndResubmit() : () => void handleWithdrawOnly()
  const primaryLabel = withdrawing ? (
    <span className="inline-flex items-center justify-center gap-2">
      <Loader2 className="h-5 w-5 animate-spin" />
      {t("sendingCta")}
    </span>
  ) : canResubmit && newAmountLabel != null ? (
    t("withdrawResubmitCta", { newAmount: newAmountLabel })
  ) : (
    t("withdrawCta")
  )
  const primaryDisabled = withdrawing || !offerId || resolving

  const bodyText = (
    <div className="w-full space-y-3 text-center">
      {resolving ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-foreground/50" />
        </div>
      ) : (
        <>
          {canResubmit && newAmountLabel != null ? (
            <p className="text-balance text-sm leading-relaxed text-foreground/85 text-center sm:text-sm">
              {t("bodyResubmit", { newAmount: newAmountLabel })}
            </p>
          ) : (
            <p className="text-balance text-sm leading-relaxed text-foreground/85 text-center sm:text-sm">
              {t("body")}
            </p>
          )}
          {amountLabel != null && !resolveFailed && (
            <p className="text-center text-base font-semibold tabular-nums text-foreground">
              {t("currentAmount")}: {amountLabel}
            </p>
          )}
          {resolveFailed && !resolving && (
            <p className="text-center text-sm text-red-400/90">{t("resolveError")}</p>
          )}
        </>
      )}
    </div>
  )

  const actionButtons = (
    <div className="mx-auto flex w-full max-w-md flex-col gap-2">
      <Button
        type="button"
        className="h-12 w-full rounded-full bg-amber-600 text-base font-medium text-white hover:bg-amber-600/90 disabled:opacity-50"
        disabled={primaryDisabled}
        onClick={primaryAction}
      >
        {primaryLabel}
      </Button>
      {canResubmit ? (
        <Button
          type="button"
          variant="secondary"
          className="h-11 w-full rounded-full border border-foreground/20 bg-foreground/10 text-foreground hover:bg-foreground/15"
          disabled={withdrawing || !offerId || resolving}
          onClick={() => void handleWithdrawOnly()}
        >
          {t("withdrawOnlyCta")}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        className="h-11 w-full rounded-full text-foreground/80 hover:bg-foreground/10 hover:text-foreground"
        disabled={withdrawing}
        onClick={() => onOpenChange(false)}
      >
        {t("dismiss")}
      </Button>
    </div>
  )

  if (!isMobile) {
    return (
      <DialogCard
        open={open}
        onOpenChange={onOpenChange}
        nested
        title={t("title")}
        panelClassName="max-w-md max-h-[min(90dvh,720px)]"
        footer={actionButtons}
      >
        {bodyText}
      </DialogCard>
    )
  }

  return (
    <DockSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
      zIndex={SHEET_Z_NESTED}
      footer={actionButtons}
    >
      <motion.div
        className="w-full text-center"
        initial={innerUp}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...innerEase, delay: reduceMotion ? 0 : 0.04 }}
      >
        {bodyText}
      </motion.div>
    </DockSheet>
  )
}
